import { SettlementUnitPicker } from './SettlementUnitPicker';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import { ViewSidebar } from '@/components/ViewSidebar';
import { Button, IconButton } from '@/components/Button';
import { Alert, Empty, ErrorState, Skeleton } from '@/components/States';
import { money, num } from '@/lib/format';
import { defaultBasis, parsePeriod, periodParam, periodQuery, useGet, useStlPerms, useUrlState, thisMonth, type Period } from './api';
import { DATE_BASIS, EPS, LABELS, matchView, type BalanceView } from './status';
import type { CashRecord, InvoiceRecord, Scope, StatementListItem, SummaryPayload, SummaryRow } from './types';
import { PartnerCard, partnerCols } from './PartnerList';
import { StatementCreateDialog } from './StatementCreateDialog';
import { RecordList, sumAmount, toVM, type RecordVM } from './RecordList';
import { Chips, FilterButton, FilterSheet, Kpis, PeriodBar, SearchBox, Sheet, StlHeader, Table, bigMoney, type ChipItem, type Col, type Kpi } from './ui';
import s from './PartnersPage.module.css';

type View = BalanceView | 'overdue' | 'unallocated' | 'cash' | 'invoice';
const PAGE = 5;
const TABLET_PAGE = 5;
const MONTHLY: Record<string, string> = { monthly: '仅月结', nonMonthly: '非月结' };

/** 各视图的计数与汇总：待结 / 预结按行的正负差额分别相加（老系统 getSettlementStatusTotals） */
function statusTotals(rows: SummaryRow[]) {
  return rows.reduce(
    (a, r) => {
      const b = num(r.balanceAmount);
      const i = num(r.invoiceBalanceAmount);
      return {
        open: a.open + (b > EPS ? b : 0),
        over: a.over + (b < -EPS ? -b : 0),
        invOpen: a.invOpen + (i > EPS ? i : 0),
        invOver: a.invOver + (i < -EPS ? -i : 0),
      };
    },
    { open: 0, over: 0, invOpen: 0, invOver: 0 },
  );
}

function csvCell(value: string | number | null | undefined) {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replaceAll('"', '""')}"`;
}

/** 客户往来 / 供应商往来：同一套页面只换字段名（高保真稿 T1 / T2） */
export function PartnersPage({ scope }: { scope: Scope }) {
  const ff = useFormFactor();
  const perms = useStlPerms();
  const nav = useNavigate();
  const [sp, set] = useUrlState();
  const L = LABELS[scope];
  const segPath = scope === 'supplier' ? 'suppliers' : 'customers';

  const period = parsePeriod(sp.get('period'));
  const basisOpts = DATE_BASIS[scope];
  const basis = basisOpts.some((o) => o.value === sp.get('basis')) ? sp.get('basis')! : defaultBasis(scope);
  const monthly = MONTHLY[sp.get('monthly') || ''] ? sp.get('monthly')! : 'all';
  const q = sp.get('q') || '';
  const canSummary = perms.overview || perms.followerView;
  const requestedView = ((sp.get('view') as View) || (canSummary ? 'all' : 'cash')) as View;
  const view = perms.followerView && ['cash', 'invoice', 'overdue', 'unallocated'].includes(requestedView) ? 'all' : requestedView;
  const recordKind = view === 'cash' || view === 'invoice' ? view : null;
  const selectedId = sp.get('id') || undefined;
  const page = Math.max(1, Number(sp.get('page')) || 1);
  const [quick, setQuick] = useState<'cash' | 'invoice' | 'allocation' | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [createTarget, setCreateTarget] = useState<SummaryRow | null>(null);
  const [createPickerOpen, setCreatePickerOpen] = useState(false);
  const [phoneSortAscending, setPhoneSortAscending] = useState(false);
  const [searching, setSearching] = useState(!!q);
  const allowed = perms.scope(scope);
  const exportPartner = (row: SummaryRow) => {
    const includeAmounts = summary.data?.totals.billAmount !== undefined;
    const headers = ['往来单位', '单位编号', '结算方式', ...(includeAmounts ? ['业务金额', scope === 'customer' ? '已收' : '已付', scope === 'customer' ? '未收' : '未付', scope === 'customer' ? '已开票' : '已收票', scope === 'customer' ? '未开票' : '未收票'] : []), '账龄'];
    const values = [row.partnerName, row.partnerCode, row.isMonthlySettlement ? `月结${row.paymentTermDays ? ` ${row.paymentTermDays} 天` : ''}${row.settlementDay ? ` · 每月 ${row.settlementDay} 日` : ''}` : '现结', ...(includeAmounts ? [row.billAmount, row.cashAmount, row.balanceAmount, row.invoiceAmount, row.invoiceBalanceAmount] : []), row.agingBucket];
    const content = `\uFEFF${headers.map(csvCell).join(',')}\r\n${values.map(csvCell).join(',')}\r\n`;
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${scope === 'customer' ? '客户' : '供应商'}结算-${period.kind === 'month' ? period.value : period.kind === 'year' ? period.value : '全部'}-${row.partnerName.replace(/[\\/:*?"<>|]/g, '_')}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const summary = useGet<SummaryPayload>(allowed && canSummary ? '/api/v2/settlements/summary' : null, {
    scope,
    ...periodQuery(period),
    dateBasis: basis,
    monthly: monthly !== 'all' ? monthly : undefined,
    keyword: q || undefined,
  });
  const followerStatements = useGet<StatementListItem[]>(allowed && perms.followerView && period.kind === 'month' ? '/api/v2/settlements/follower-statements' : null, {
    month: period.kind === 'month' ? period.value : undefined,
  });
  const otherScope: Scope = scope === 'customer' ? 'supplier' : 'customer';
  const otherSummary = useGet<SummaryPayload>(
    (ff === 'phone' || ff === 'tablet') && !perms.followerView && canSummary && perms.scope(otherScope) ? '/api/v2/settlements/summary' : null,
    { scope: otherScope, ...periodQuery(period), dateBasis: defaultBasis(otherScope) },
  );
  const customerStatements = useGet<StatementListItem[]>(
    (ff === 'phone' || ff === 'tablet') && perms.statement && !perms.followerView ? '/api/settlements/statements' : null,
    { list: 1, scope: 'customer', month: period.kind === 'month' ? period.value : undefined },
  );
  const supplierStatements = useGet<StatementListItem[]>(
    (ff === 'phone' || ff === 'tablet') && perms.statement && !perms.followerView ? '/api/settlements/statements' : null,
    { list: 1, scope: 'supplier', month: period.kind === 'month' ? period.value : undefined },
  );
  const records = useGet<(CashRecord | InvoiceRecord)[]>(
    allowed && recordKind ? `/api/settlements/${recordKind === 'cash' ? 'cash-records' : 'invoice-records'}` : null,
    { partnerType: scope === 'supplier' ? 'SUPPLIER' : 'CUSTOMER', ...periodQuery(period) },
  );

  const rows = useMemo(() => summary.data?.rows ?? [], [summary.data]);
  const followerSegmentCounts = perms.followerView && period.kind === 'month' ? {
    customer: summary.fresh && summary.data ? rows.length : undefined,
    statement: followerStatements.fresh ? followerStatements.data?.length : undefined,
  } : undefined;
  const segmentCounts = ff === 'phone' || ff === 'tablet' ? {
    customer: perms.followerView
      ? followerSegmentCounts?.customer
      : scope === 'customer' ? summary.fresh ? rows.length : undefined : otherSummary.fresh ? otherSummary.data?.rows.length : undefined,
    supplier: perms.followerView
      ? undefined
      : scope === 'supplier' ? summary.fresh ? rows.length : undefined : otherSummary.fresh ? otherSummary.data?.rows.length : undefined,
    statement: perms.followerView
      ? followerSegmentCounts?.statement
      : customerStatements.fresh && supplierStatements.fresh
        ? (customerStatements.data?.length || 0) + (supplierStatements.data?.length || 0)
        : undefined,
  } : followerSegmentCounts;
  const amounts = summary.data?.totals.billAmount !== undefined;
  const shown = recordKind ? [] : rows.filter((r) => view === 'unallocated' ? (r.unallocatedRecordCount ?? 0) > 0 : view === 'overdue' ? (r.overdueOrderCount ?? 0) > 0 : matchView(view as BalanceView, r));
  const phoneRows = [...shown].sort((a, b) => {
    const amountOrder = num(a.balanceAmount) - num(b.balanceAmount);
    return (phoneSortAscending ? amountOrder : -amountOrder) || a.partnerName.localeCompare(b.partnerName, 'zh-CN');
  });
  const recordRows: RecordVM[] = useMemo(() => {
    const kw = q.toLowerCase();
    return (records.data ?? []).map((r) => toVM(r)).filter((r) =>
      !kw || [r.partnerName, r.partnerCode, r.invoiceNo, r.referenceNo, r.note].some((x) => x?.toLowerCase().includes(kw)),
    );
  }, [records.data, q]);

  // 手机 / iPad 列表栏：选中的 chip 滚到可见位置
  const chipsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    chipsRef.current?.querySelector('[aria-pressed="true"]')?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [view, ff]);

  if (!allowed) {
    return (
      <div className={s.page}>
        <Empty icon="lock" title={`没有查看${L.title}的权限`} action={<Link to="/settlements" className={s.linkBtn}>回结算总览</Link>}>
          需要「查看{L.partner}结算」权限，请联系管理员在老版的角色设置里开通。
        </Empty>
      </div>
    );
  }

  /* ---------------- URL 写入 ---------------- */
  const setPeriod = (p: Period) => set({ period: periodParam(p), page: undefined });
  const detailQuery = new URLSearchParams(
    Object.entries({ period: periodParam(period), basis: basis !== defaultBasis(scope) ? basis : undefined }).filter(([, v]) => v) as [string, string][],
  ).toString();
  const detailTo = (id: string) => `/settlements/${segPath}/${id}${detailQuery ? `?${detailQuery}` : ''}`;
  /* ---------------- chips ---------------- */
  const count = (v: BalanceView) => rows.filter((r) => matchView(v, r)).length;
  const chipItems: ChipItem[] = [];
  if (canSummary) {
    chipItems.push({ key: 'all', label: '全部', count: summary.data ? rows.length : undefined });
    if (amounts && summary.data?.totals.overduePartnerCount !== undefined) {
      chipItems.push({ key: 'overdue', label: scope === 'customer' ? '逾期未收' : '逾期未付', count: summary.data.totals.overduePartnerCount });
    }
    if (amounts || rows.some((r) => r.cashStatus !== undefined)) {
      const opt: [BalanceView, string, boolean][] = [
        ['open', amounts ? L.open : '未结清', true],
        ['invOpen', L.invOpen, true],
        ['over', L.over, false],
        ['invOver', L.invOver, false],
        ['settled', '已结清', true],
      ];
      for (const [k, label, always] of opt) {
        const n = count(k);
        const touchLayout = ff === 'phone' || ff === 'tablet';
        if (always || n > 0 || view === k || (touchLayout && k === 'over')) {
          chipItems.push({ key: k, label: k === 'open' ? '未结清' : touchLayout && k === 'over' ? '超付/超收' : label, count: summary.data ? n : undefined });
        }
      }
    }
    if (amounts && summary.data?.totals.unallocatedRecordCount !== undefined) {
      chipItems.push({ key: 'unallocated', label: '待核销台账', count: summary.data.totals.unallocatedRecordCount });
    }
  }
  // iPad T1/T2 keeps the source's compact balance-status row. Record views remain
  // available from the desktop/phone flows; the tablet header already exposes
  // registration actions and these extra chips otherwise crowd the period controls.
  if (!perms.followerView && ff !== 'tablet') chipItems.push({ key: 'cash', label: L.cashRecord, sep: canSummary }, { key: 'invoice', label: L.invRecord });
  const touchChipOrder = ['all', 'overdue', 'invOpen', 'over', 'unallocated'];
  const orderedTouchChips = touchChipOrder.flatMap((key) => { const item = chipItems.find((x) => x.key === key); return item ? [item] : []; });
  const touchChipItems = ff === 'tablet'
    ? orderedTouchChips
    : ff === 'phone'
      ? [...orderedTouchChips, ...chipItems.filter((item) => !touchChipOrder.includes(item.key))]
      : chipItems;
  const chips = (
    <div ref={chipsRef} className={s.chipsBox}>
      <Chips items={touchChipItems} value={view} onChange={(k) => set({ view: k === 'all' ? undefined : k, id: undefined, page: undefined })} />
    </div>
  );

  /* ---------------- 筛选 ---------------- */
  const filterCount = (period.kind !== 'month' ? 1 : 0) + (basis !== defaultBasis(scope) ? 1 : 0) + (monthly !== 'all' ? 1 : 0);
  const filterBtn = (boxed?: boolean) => <FilterButton count={filterCount} boxed={boxed} onClick={() => setFilterOpen(true)} />;
  const applied = filterCount > 0 && (
    <div className={s.applied}>
      {period.kind !== 'month' && <button className={s.achip} onClick={() => setPeriod({ kind: 'month', value: thisMonth() })}>{period.kind === 'all' ? '全部时间' : '按年'}<Icon name="x" size={14} /></button>}
      {basis !== defaultBasis(scope) && <button className={s.achip} onClick={() => set({ basis: undefined })}>按{basisOpts.find((o) => o.value === basis)?.label}<Icon name="x" size={14} /></button>}
      {monthly !== 'all' && <button className={s.achip} onClick={() => set({ monthly: undefined })}>{MONTHLY[monthly]}<Icon name="x" size={14} /></button>}
    </div>
  );
  const filterSheet = filterOpen && (
    <FilterSheet
      sheet={ff === 'phone'}
      onClose={() => setFilterOpen(false)}
      value={{ kind: period.kind, basis, monthly }}
      defaults={{ kind: 'month', basis: defaultBasis(scope), monthly: 'all' }}
      groups={[
        { key: 'kind', title: '统计区间', required: true, opts: [{ value: 'month', label: '按月' }, { value: 'year', label: '按年' }, { value: 'all', label: '全部时间' }] },
        { key: 'basis', title: '日期口径', required: true, opts: basisOpts },
        { key: 'monthly', title: '结算方式', opts: [{ value: 'all', label: '全部' }, { value: 'monthly', label: '仅月结' }, { value: 'nonMonthly', label: '非月结' }] },
      ]}
      hint={`日期口径只决定哪些${L.docs}落在这段时间里；${L.cashVerb}和${L.invVerb}记录按发生日期统计。结算方式只作用于往来单位汇总。`}
      onApply={(f) => {
        setFilterOpen(false);
        const p: Period = f.kind === period.kind ? period : f.kind === 'all' ? { kind: 'all' } : f.kind === 'year' ? { kind: 'year', value: String(new Date().getFullYear()) } : { kind: 'month', value: thisMonth() };
        set({ period: periodParam(p), basis: f.basis !== defaultBasis(scope) ? f.basis : undefined, monthly: f.monthly !== 'all' ? f.monthly : undefined, page: undefined });
      }}
    />
  );

  /* ---------------- 汇总卡：月份 + 口径常驻在顶上 ---------------- */
  const T = summary.data?.totals;
  const st = statusTotals(rows);
  const shownStatus = statusTotals(shown);
  const shownBill = shown.reduce((total, row) => total + num(row.billAmount), 0);
  const kpis: Kpi[] = recordKind
    ? [
        { label: '记录', value: `${recordRows.length} 笔` },
        ...(recordRows.some((r) => r.amount !== undefined) ? [{ label: `${recordKind === 'cash' ? L.cashVerb : L.invVerb}合计`, value: bigMoney(sumAmount(recordRows)), tone: 'blue' as const }] : []),
        { label: `涉及${L.partner}`, value: `${new Set(recordRows.map((r) => r.partnerName)).size} 家` },
      ]
    : !T
      ? []
      : amounts
        ? [
            { label: L.bill, value: bigMoney(T.billAmount) },
            { label: L.cash, value: bigMoney(T.cashAmount), tone: 'blue' },
            { label: L.openAmount, value: bigMoney(st.open), tone: st.open > EPS ? 'bad' : 'mute', sub: `${count('open')} 家` },
            ...(st.over > EPS ? [{ label: L.overAmount, value: bigMoney(st.over), tone: 'over' as const, sub: `${count('over')} 家` }] : []),
            { label: L.invRequired, value: bigMoney(T.invoiceRequiredAmount) },
            { label: L.inv, value: bigMoney(T.invoiceAmount) },
            { label: L.invOpenAmount, value: bigMoney(st.invOpen), tone: st.invOpen > EPS ? 'warn' : 'mute', sub: `${count('invOpen')} 家` },
            ...(st.invOver > EPS ? [{ label: L.invOverAmount, value: bigMoney(st.invOver), tone: 'blue' as const, sub: `${count('invOver')} 家` }] : []),
          ]
        : [
            { label: `相关${L.partner}`, value: `${rows.length} 家` },
            { label: '未结清', value: `${count('open')} 家` },
            { label: L.invOpen, value: `${count('invOpen')} 家` },
            { label: perms.followerView ? '本月对账单' : L.docs, value: perms.followerView ? `${rows.filter((r) => r.currentStatement).length} 份` : `${T.orderCount} 张` },
          ];
  // iPad 列表栏只有 300–360 宽：汇总卡只留应结、待结、待票三格，其余在详情里看
  const compact: string[] = [L.bill, L.openAmount, L.invOpenAmount];
  const sumCard = (narrow?: boolean) => {
    const items = ff === 'phone' && !recordKind && amounts && T ? [
      { label: scope === 'customer' ? '应收合计' : '应付合计', value: bigMoney(T.billAmount) },
      { label: scope === 'customer' ? '未开票' : '未收票', value: bigMoney(st.invOpen), tone: 'warn' as const },
      { label: scope === 'customer' ? '逾期未收' : '逾期未付', value: T.overduePartnerCount === undefined ? '—' : `${T.overduePartnerCount} 家`, tone: 'bad' as const },
    ] : narrow && !recordKind && amounts ? kpis.filter((k) => compact.includes(String(k.label))) : kpis;
    return (
      <div className={s.sum}>
        <PeriodBar period={period} onPeriod={setPeriod} basis={basis} basisOptions={recordKind ? undefined : basisOpts} onBasis={(v) => set({ basis: v !== defaultBasis(scope) ? v : undefined })} basisText={recordKind ? '发生日期' : undefined} />
        {items.length > 0 && <Kpis items={items} className={ff === 'phone' && !recordKind ? s.phoneSummaryKpis : undefined} />}
      </div>
    );
  };

  /* ---------------- 列表主体 ---------------- */
  const active = recordKind ? records : summary;
  const noSummaryHint = !canSummary && (
    <div className={s.hintBox}>
      <Alert tone="info" title="没有「查看结算总览」权限，看不到按单位的汇总">{L.cashRecord}和{L.invRecord}仍然可以查看。</Alert>
    </div>
  );
  const body: ReactNode = (() => {
    if (active.error && !active.data) {
      return <ErrorState title={`${recordKind ? (recordKind === 'cash' ? L.cashRecord : L.invRecord) : L.title}加载失败`} message={active.error.message} detail={`GET ${active.url || ''}`} onRetry={active.reload} />;
    }
    if (active.loading && !active.data) return <div className={s.pad}><Skeleton /><Skeleton lines={3} /></div>;
    if (recordKind) {
      if (!recordRows.length) {
        return <Empty icon="receipt" title={q ? `没有符合「${q}」的记录` : `这段时间没有${recordKind === 'cash' ? L.cashRecord : L.invRecord}`}>换个月份，或者切到「全部时间」看看。</Empty>;
      }
      return null;
    }
    if (!shown.length) {
      return (
        <Empty icon="ok" title={q ? `没有符合「${q}」的${L.partner}` : rows.length ? '这个视图里没有单位' : `这段时间没有${L.partner}往来`}>
          {rows.length ? '别的视图里还有单位，或者清空筛选再看看。' : '换个月份或日期口径再看看；日期口径决定哪些单据落在这个月。'}
        </Empty>
      );
    }
    return null;
  })();
  const capped = recordKind && (records.data?.length ?? 0) >= 200 && <p className={s.cap}>老接口一次最多返回最近 200 条，更早的记录请缩小时间范围。</p>;

  const listbar = (
    <div className={s.listbar}>
      {recordKind
        ? <span>{recordRows.length} 笔 · 按发生日期倒序</span>
        : <span>{shown.length} 家{q && ` · 搜索「${q}」`}</span>}
      {!recordKind && amounts && <button type="button" className={s.sortButton} aria-label={`${scope === 'customer' ? '未收金额' : '未付金额'}，${phoneSortAscending ? '从低到高' : '从高到低'}；点击切换`} onClick={() => setPhoneSortAscending((ascending) => !ascending)}>{scope === 'customer' ? '未收金额' : '未付金额'}<Icon name="sort" size={14} /></button>}
      {active.loading && active.data && <span className={s.loadingDot}>更新中…</span>}
    </div>
  );

  const searchBox = (cls?: string) => (
    <SearchBox key={q} className={cls} value={q} placeholder={recordKind ? `${L.partner} / 发票号 / 流水号 / 备注` : `${L.partner}名称 / 编码`} onSubmit={(v) => set({ q: v || undefined, id: undefined, page: undefined })} />
  );
  const writeLinks = (
    <>
      {perms.invoiceWrite(scope) && <Button icon="plus" onClick={() => setQuick('invoice')}>登记{L.invVerb}</Button>}
      {perms.cashWrite(scope) && <Button icon="plus" variant="primary" onClick={() => setQuick('cash')}>登记{L.cashVerb}</Button>}
      {perms.allocation && <Button icon="ok" onClick={() => setQuick('allocation')}>自动核销</Button>}
    </>
  );

  const quickPicker = quick && <SettlementUnitPicker initialScope={scope} month={period.kind === 'month' ? period.value : thisMonth()} action={quick} onClose={() => setQuick(null)} />;

  /* ------------------------------ 手机 ------------------------------ */
  if (ff === 'phone') {
    return (
      <div className={s.phone}>
        <StlHeader phone active={scope} keep={periodParam(period)} counts={segmentCounts} actions={<>{perms.statementWrite && <IconButton name="plus" label="生成对账单" onClick={() => setCreatePickerOpen(true)} />}<IconButton name="srch" label="搜索" onClick={() => setSearching((x) => !x)} /></>} />
        {searching && <div className={s.psearch}>{searchBox()}</div>}
        <div className={s.chipbar}>{chips}{filterBtn()}</div>
        {applied}
        <div className={s.pbody}>
          {noSummaryHint}
          {sumCard()}
          {listbar}
          {body ||
            (recordKind ? (
              <RecordList kind={recordKind} scope={scope} rows={recordRows} mode="cards" boxed />
            ) : (
              phoneRows.map((r) => <PartnerCard key={r.partnerId} row={r} scope={scope} to={perms.followerView ? undefined : detailTo(r.partnerId)} />)
            ))}
          {capped}
          <div className={s.end}>{!body && '没有更多了'}</div>
        </div>
        {filterSheet}
        {createPickerOpen && <Sheet title="生成对账单" sheet onClose={() => setCreatePickerOpen(false)}>
          <p className={s.createPickerHint}>选择{L.partner} · {period.kind === 'month' ? period.value.replace('-', ' 年 ') + ' 月' : thisMonth().replace('-', ' 年 ') + ' 月'}</p>
          {rows.map((row) => {
            const balance = num(row.balanceAmount);
            const balanceLabel = balance > EPS ? (scope === 'customer' ? '待收' : '待付') : balance < -EPS ? (scope === 'customer' ? '多收' : '多付') : '已结清';
            return <button key={row.partnerId} type="button" className={s.createPartnerOption} onClick={() => { setCreatePickerOpen(false); setCreateTarget(row); }}>
              <span><b>{row.partnerName}</b><small>{L.partner} · {money(Math.abs(balance))} {balanceLabel}</small></span><Icon name="chev" size={16}/>
            </button>;
          })}
          {!rows.length && <p className={s.createPickerHint}>当前月份没有可生成对账单的{L.partner}。</p>}
        </Sheet>}
        {createTarget && <StatementCreateDialog scope={scope} partnerId={createTarget.partnerId} month={period.kind === 'month' ? period.value : thisMonth()} dateBasis={basis} partnerName={createTarget.partnerName} canConfirm={perms.confirm} onClose={() => setCreateTarget(null)} onDone={(id) => { setCreateTarget(null); nav(`/settlements/statements/${id}`); }} />}
      </div>
    );
  }

  /* ------------------------------ iPad：T1/T2 全宽对比表 ------------------------------ */
  if (ff === 'tablet' && !recordKind) {
    const tabletPages = Math.max(1, Math.ceil(shown.length / TABLET_PAGE));
    const tabletCurrentPage = Math.min(page, tabletPages);
    const tabletRows = shown.slice((tabletCurrentPage - 1) * TABLET_PAGE, tabletCurrentPage * TABLET_PAGE);
    const tabletKpis: Kpi[] = !T ? [] : amounts ? [
      { label: scope === 'customer' ? '应收合计' : '应付合计', value: bigMoney(T.billAmount) },
      { label: scope === 'customer' ? '未开票' : '未收票', value: bigMoney(st.invOpen), tone: 'warn' },
      { label: scope === 'customer' ? '逾期未收' : '逾期未付', value: T.overduePartnerCount === undefined ? '—' : `${T.overduePartnerCount} 家`, tone: 'bad' },
      { label: L.invOpen, value: `${count('invOpen')} 家` },
      { label: '待核销台账', value: T.unallocatedRecordCount === undefined ? '—' : `${T.unallocatedRecordCount} 笔` },
    ] : [
      { label: `相关${L.partner}`, value: `${rows.length} 家` },
      { label: '未结清', value: `${count('open')} 家` },
      { label: L.invOpen, value: `${count('invOpen')} 家` },
      { label: perms.followerView ? '本月对账单' : L.docs, value: perms.followerView ? `${rows.filter((r) => r.currentStatement).length} 份` : `${T.orderCount} 张` },
    ];
    const tabletWidths: Record<string, number> = {
      select: 44, name: 180, terms: 132, bill: 114, cash: 106, open: 106,
      inv: 102, invOpen: 106, age: 90, action: 64,
    };
    const tabletBaseCols: Col<SummaryRow>[] = [
      ...(perms.statementWrite ? [{
        key: 'select', title: '', w: 44,
        render: (r) => <input type="radio" name="settlement-partner" aria-label={`选择${r.partnerName}`} checked={selectedId === r.partnerId} onChange={() => set({ id: r.partnerId })} onClick={(e) => e.stopPropagation()} />,
      } satisfies Col<SummaryRow>] : []),
      ...partnerCols(scope, tabletRows).filter((c) => c.key !== 'action'),
      ...(!perms.followerView ? partnerCols(scope, tabletRows).filter((c) => c.key === 'action') : []),
    ];
    const tabletCols = tabletBaseCols.map((col) => ({ ...col, w: tabletWidths[col.key] ?? col.w, left: col.key === 'name' && perms.statementWrite ? 44 : col.left }));
    const selected = shown.find((r) => r.partnerId === selectedId);
    return (
      <div className={`${s.page} ${s.tabletPage}`}>
        <StlHeader phone={false} active={scope} keep={periodParam(period)} counts={segmentCounts} actions={<>{searchBox(s.tabletSearch)}{writeLinks}{quickPicker}</>} />
        <div className={s.tabletBar}>
          {chips}
          <PeriodBar period={period} onPeriod={setPeriod} basis={basis} basisOptions={basisOpts} onBasis={(v) => set({ basis: v !== defaultBasis(scope) ? v : undefined })} />
          {filterBtn(true)}
        </div>
        {applied}
        {tabletKpis.length > 0 && <Kpis items={tabletKpis} className={s.tabletKpis} />}
        <div className={s.tabletSelected}>
          <span>{selected ? `已选 1 家 · ${selected.partnerName}` : perms.statementWrite ? `勾选${L.partner}后可生成对账单` : `共 ${shown.length} 家${L.partner}`}</span>
          {selected && perms.statementWrite && (period.kind === 'month' ? <Button size="sm" onClick={() => setCreateTarget(selected)}>生成对账单</Button> : <Button size="sm" disabled disabledReason="请先选择结算月份">生成对账单</Button>)}
          {selected && perms.businessExport && <Button size="sm" onClick={() => exportPartner(selected)}>导出</Button>}
          <span className={s.sp} />
          <span>{shown.length} 家 · {amounts ? `按${L.open}金额倒序` : '按名称排序'}</span>
        </div>
        {body ? <div className={s.tabletBody}>{body}</div> : <Table cols={tabletCols} rows={tabletRows} rowKey={(r) => r.partnerId} onRow={perms.followerView ? undefined : (r) => nav(detailTo(r.partnerId))} selected={selectedId} />}
        {!body && <footer className={s.pager}>
          <span>第 {shown.length ? (tabletCurrentPage - 1) * TABLET_PAGE + 1 : 0}–{Math.min(tabletCurrentPage * TABLET_PAGE, shown.length)} 条 / 共 {shown.length} 家{T && amounts ? ` · ${L.openAmount} ${money(shownStatus.open)} · ${L.invOpen} ${money(shownStatus.invOpen)}` : ''}</span>
          <span className={s.sp} />
          <Button size="sm" disabled={tabletCurrentPage <= 1} onClick={() => set({ page: tabletCurrentPage - 1 > 1 ? String(tabletCurrentPage - 1) : undefined, id: undefined })}>上一页</Button>
          <span className="tnum">{tabletCurrentPage} / {tabletPages}</span>
          <Button size="sm" disabled={tabletCurrentPage >= tabletPages} onClick={() => set({ page: String(tabletCurrentPage + 1), id: undefined })}>下一页</Button>
        </footer>}
        {filterSheet}
        {createTarget && period.kind === 'month' && <StatementCreateDialog scope={scope} partnerId={createTarget.partnerId} month={period.value} dateBasis={basis} partnerName={createTarget.partnerName} canConfirm={perms.confirm} onClose={() => setCreateTarget(null)} onDone={(id) => { setCreateTarget(null); nav(`/settlements/statements/${id}`); }} />}
      </div>
    );
  }

  /* --------------------------- 桌面 / iPad 台账表格 --------------------------- */
  const pages = Math.max(1, Math.ceil(shown.length / PAGE));
  const cur = Math.min(page, pages);
  const pageRows = shown.slice((cur - 1) * PAGE, cur * PAGE);
  const pageTokens: (number | 'ellipsis')[] = pages <= 4 ? Array.from({ length: pages }, (_, i) => i + 1)
    : cur <= 2 ? [1, 2, 'ellipsis', pages]
      : cur >= pages - 1 ? [1, 'ellipsis', pages - 1, pages]
        : [1, 'ellipsis', cur - 1, cur, cur + 1, 'ellipsis', pages];
  const desktopCanPick = perms.statementWrite || perms.businessExport;
  const selected = shown.find((r) => r.partnerId === selectedId);
  const desktopWidths: Record<string, number> = { select: 41, name: 176, terms: 126, bill: 129, cash: 129, open: 129, inv: 129, invOpen: 129, age: 119, action: 108 };
  const desktopCols: Col<SummaryRow>[] = [
    ...(desktopCanPick ? [{
      key: 'select', title: '', w: 44, align: 'c' as const, fix: true,
      render: (r: SummaryRow) => <input type="checkbox" aria-label={`选择${r.partnerName}`} checked={selectedId === r.partnerId} onChange={() => set({ id: selectedId === r.partnerId ? undefined : r.partnerId })} onClick={(e) => e.stopPropagation()} />,
    }] : []),
    ...partnerCols(scope, pageRows).filter((c) => !perms.followerView || c.key !== 'action').map((c) => ({ ...c, w: desktopCanPick ? desktopWidths[c.key] ?? c.w : c.w, left: c.key === 'name' && desktopCanPick ? 44 : c.left })),
  ];
  return (
    <div className={`${s.page} ${s.desktopPage} ${desktopCanPick ? s.desktopSelectable : ''}`}>
      <header className={s.desktopHead}>
        <div><h1>{scope === 'customer' ? '客户结算' : '供应商结算'}</h1><p>{scope === 'customer' ? '按客户汇总业务金额、已收、未收、开票情况' : '按供应商汇总采购金额、已付、未付、收票情况'} <span>共 {rows.length} 家{T && amounts ? ` · ${scope === 'customer' ? '应收' : '应付'} ${money(st.open)}` : ''}</span></p></div>
        <div className={s.desktopActions}>{writeLinks}{quickPicker}</div>
      </header>
      <div className={s.desktopFilters}>
        <div className={s.scopeLinks}><Link className={scope === 'customer' ? s.scopeOn : ''} to="/settlements/customers">客户往来</Link><Link className={scope === 'supplier' ? s.scopeOn : ''} to="/settlements/suppliers">供应商往来</Link></div>
        <PeriodBar period={period} onPeriod={setPeriod} basis={basis} basisOptions={recordKind ? undefined : basisOpts} onBasis={(v) => set({ basis: v !== defaultBasis(scope) ? v : undefined })} basisText={recordKind ? '发生日期' : undefined} />
        <span className={s.sp} />
        <Button icon="ref" onClick={active.reload}>刷新</Button>
      </div>
      <div className={s.desktopChips}>
        <div className={s.monthlyChips}>{(['all', 'monthly', 'nonMonthly'] as const).map((k) => <button key={k} type="button" className={monthly === k ? s.monthlyOn : ''} aria-pressed={monthly === k} onClick={() => set({ monthly: k === 'all' ? undefined : k, page: undefined })}>{k === 'all' ? '全部' : k === 'monthly' ? '月结' : '现结'}{k === 'all' ? ` ${rows.length}` : ''}</button>)}</div>
        {chips}
      </div>
      <div className={s.desktopWork}>
        <ViewSidebar title="结算视图" active={recordKind || (view === 'all' ? scope : view)} onPick={(key) => set({ view: key === scope ? undefined : key, page: undefined })} views={[
          ...(perms.customer ? [{ key: 'customer', label: '客户结算', icon: 'team', to: '/settlements/customers', count: scope === 'customer' ? rows.length : undefined }] : []),
          ...(perms.supplier ? [{ key: 'supplier', label: '供应商结算', icon: 'inbox', to: '/settlements/suppliers', count: scope === 'supplier' ? rows.length : undefined }] : []),
          { key: 'open', label: scope === 'customer' ? '待收' : '待付', icon: 'alert', count: count('open'), separator: true },
          ...(amounts && T?.overduePartnerCount !== undefined ? [{ key: 'overdue', label: scope === 'customer' ? '逾期未收' : '逾期未付', icon: 'alert', count: T.overduePartnerCount }] : []),
          { key: 'invOpen', label: L.invOpen, icon: 'book', count: count('invOpen') },
          { key: 'over', label: scope === 'customer' ? '超收' : '超付', icon: 'flag', count: count('over') },
          ...(amounts && T?.unallocatedRecordCount !== undefined ? [{ key: 'unallocated', label: '待核销台账', icon: 'receipt', count: T.unallocatedRecordCount }] : []),
          ...(!perms.followerView ? [
            { key: 'cash', label: L.cashRecord, icon: 'receipt', separator: true },
            { key: 'invoice', label: L.invRecord, icon: 'book' },
          ] : []),
          ...(perms.statement || perms.followerView ? [{ key: 'statement', label: '对账单', icon: 'cards', to: '/settlements/statements', separator: true }] : []),
        ]} />
        <section className={s.desktopArea}>
          <div className={s.desktopToolbar}>
            {searchBox(s.dsearch)}
            <div className={s.desktopSelection}>
              <span>{selected ? `已选 1 家 · ${selected.partnerName}` : (perms.statementWrite || perms.businessExport) ? `选择${L.partner}后可生成对账单` : `共 ${shown.length} 家${L.partner}`}</span>
              {selected && perms.statementWrite && (period.kind === 'month' ? <Button size="sm" onClick={() => setCreateTarget(selected)}>生成对账单</Button> : <Button size="sm" disabled disabledReason="请先选择结算月份">生成对账单</Button>)}
              {selected && perms.businessExport && <Button size="sm" onClick={() => exportPartner(selected)}>导出</Button>}
            </div>
          </div>
          {applied}{noSummaryHint}
          {body ? <div className={s.desktopBody}>{body}</div> : recordKind ? <div className={s.desktopBody}><RecordList kind={recordKind} scope={scope} rows={recordRows} mode="table" />{capped}</div> : <Table cols={desktopCols} rows={pageRows} rowKey={(r) => r.partnerId} onRow={perms.followerView ? undefined : (r) => nav(detailTo(r.partnerId))} selected={selectedId} />}
          {!body && !recordKind && <footer className={s.pager}>
            <span>第 {(cur - 1) * PAGE + 1}–{(cur - 1) * PAGE + pageRows.length} 条 / 共 {shown.length} 家{T && amounts ? ` · ${scope === 'customer' ? '应收合计' : '应付合计'} ${money(shownBill)} · ${L.invOpen} ${money(shownStatus.invOpen)}` : ''}</span>
            <span className={s.sp} />
            <Button size="sm" aria-label="上一页" disabled={cur <= 1} onClick={() => set({ page: cur - 1 > 1 ? String(cur - 1) : undefined })}>‹</Button>
            <nav className={s.pageNums} aria-label="分页">{pageTokens.map((token, i) => token === 'ellipsis'
              ? <span key={`ellipsis-${i}`} className={s.pageEllipsis}>…</span>
              : <Button key={token} size="sm" className={s.pageNum} variant={cur === token ? 'primary' : 'default'} aria-current={cur === token ? 'page' : undefined} onClick={() => set({ page: token > 1 ? String(token) : undefined })}>{token}</Button>)}</nav>
            <Button size="sm" aria-label="下一页" disabled={cur >= pages} onClick={() => set({ page: String(cur + 1) })}>›</Button>
          </footer>}
        </section>
      </div>
      {filterSheet}
    </div>
  );
}
